/**
 * Mixer - a composite channel-strip bank driving Ableton's mixer through the
 * OP-AbletonMixer M4L hub (DEC-M1, MIXER-PAGE-DESIGN.md). One vertical fader +
 * mute + name (+ optional meter) per track. v0.1 = one 8-track bank.
 *
 * Control (iPad -> Live) dispatches OSC via `sendMixerVolume` / `sendMixerMute`
 * (primary bridge -> device udpreceive 11230), same spine as Transpose. Live
 * per-strip state lives in `mixer/session.ts` (keyed by absolute track index),
 * NOT on the item - so before the feedback half lands the strips render the
 * operator's last local action (optimistic) with `TR{n}` name placeholders.
 *
 * Fader value = Live's normalized volume 0..1, position 1:1 with the throw
 * (DEC-M2); a unity tick sits at 0.85 with a magnet detent; the readout is an
 * approximate dB (display-only, `mixer/faderEdit.ts`).
 *
 * Rate limiting (MIXER-PAGE-DESIGN.md 4.5): each strip keeps <=1 send in-flight
 * + 1 pending (last-value-wins) so a fast drag never backs up the wire on a
 * slow bridge; the final value is force-sent on release. Edit mode yields to
 * the EditableItem wrapper (`editOwnsGesture`); performance-mode `guard` long-presses
 * to grab, mirroring Fader (DEC-072).
 */

import { createSignal, createEffect, onCleanup, Show, Index, type Accessor } from 'solid-js';
import { sendMixerVolume, sendMixerRescan, sendMixerPan, sendMixerSend, fanoutTargets } from '../opbridge/dispatch.ts';
import {
  stripState, setStripVolumeOptimistic, setStripMuteLocal, stripLabel,
  setStripSoloLocal, setStripArmLocal, setStripPanOptimistic, setStripSendOptimistic, MASTER_IDX, RETURN_BASE,
} from '../mixer/session.ts';
import { positionToValue, valueToPosition, applyUnityMagnet, dbLabel, UNITY } from '../mixer/faderEdit.ts';
import { mixerFeedback } from '../mixer/statusPoll.ts';
import { editOwnsGesture, isEditMode, effectiveMixerStrips, type MixerItem, type LayoutColor } from '../layout/store.ts';
import { GUARD_GRAB_HOLD_MS, GUARD_GRAB_MOVE_TOL } from '../performance/store.ts';

interface MixerProps {
  item: MixerItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/**
 * Per-control send guard: <=1 send in-flight + 1 pending (last-value-wins), with
 * a MIN_SEND_MS floor. Same shape as the volume guard inlined in MixerStrip
 * (kept separate there so the confirmed-working two-way volume path stays
 * untouched); this factory serves the v0.2 stub continuous controls (pan +
 * sends), each of which needs its own independent guard.
 */
interface SendGuard { schedule(v: number): void; force(v: number): void; cleanup(): void }
function makeSendGuard(send: (v: number) => Promise<unknown>): SendGuard {
  let inFlight = false;
  let pending: number | null = null;
  let timer: number | undefined;
  const MIN_SEND_MS = 30;
  function fire(): void {
    timer = undefined;
    if (inFlight || pending === null) return;
    const v = pending; pending = null; inFlight = true;
    void send(v).finally(() => {
      inFlight = false;
      if (pending !== null && timer === undefined) timer = window.setTimeout(fire, MIN_SEND_MS);
    });
  }
  return {
    schedule(v) { pending = v; if (inFlight || timer !== undefined) return; fire(); },
    force(v) { pending = v; if (timer !== undefined) { clearTimeout(timer); timer = undefined; } if (!inFlight) fire(); },
    cleanup() { if (timer !== undefined) clearTimeout(timer); },
  };
}

/**
 * A thin horizontal slider used by the pan + send controls. Absolute-position
 * (tap anywhere = jump), double-tap/double-click resets to `resetTo` (matches
 * the fader's reset-to-unity feel), optional `detent` magnet (pan snaps to
 * center). Pointer-based so it works with iPad touch. The parent owns the
 * optimistic state + the guarded dispatch via `onInput` (every frame) and
 * `onCommit` (release / reset).
 *
 * Gesture gating mirrors the fader: `editLocked` (wrapper owns the gesture in
 * edit mode — never interact); `guarded` (performance-mode guard — a quick brush does
 * nothing, a long-press grabs before it responds, DEC-072).
 */
function HSlider(props: {
  value: number;
  min: number; max: number;
  resetTo: number;
  detent?: number;
  klass: string;
  label?: string;
  editLocked: () => boolean;
  guarded: () => boolean;
  onInput: (v: number) => void;
  onCommit: (v: number) => void;
}) {
  const [dragging, setDragging] = createSignal(false);
  const [grabbed, setGrabbed] = createSignal(false);
  let el: HTMLDivElement | undefined;
  const DBL_MS = 300;
  const DBL_TOL_PX = 24;
  let lastDownAt = 0;
  let lastDownX = Number.NaN;

  // Show-mode guard: long-press-to-grab, re-lock on release (mirrors Fader).
  let grabTimer: number | undefined;
  let grabStartX = 0;
  let grabStartY = 0;
  function beginPendingGrab(x: number, y: number): void {
    grabStartX = x; grabStartY = y;
    if (grabTimer !== undefined) clearTimeout(grabTimer);
    grabTimer = window.setTimeout(() => {
      grabTimer = undefined;
      setGrabbed(true);
      setDragging(true);
      props.onInput(valueFromX(grabStartX));
    }, GUARD_GRAB_HOLD_MS);
  }
  function cancelPendingGrab(): void {
    if (grabTimer !== undefined) { clearTimeout(grabTimer); grabTimer = undefined; }
  }
  onCleanup(() => { if (grabTimer !== undefined) clearTimeout(grabTimer); });
  createEffect(() => { if (!props.guarded()) { cancelPendingGrab(); setGrabbed(false); } });

  function valueFromX(clientX: number): number {
    if (!el) return props.value;
    const rect = el.getBoundingClientRect();
    let frac = (clientX - rect.left) / rect.width;
    frac = Math.max(0, Math.min(1, frac));
    let v = props.min + frac * (props.max - props.min);
    if (props.detent !== undefined) {
      const win = (props.max - props.min) * 0.04;
      if (Math.abs(v - props.detent) <= win) v = props.detent;
    }
    return v;
  }

  function down(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (props.editLocked()) return;
    e.stopPropagation();
    e.preventDefault();
    if (props.guarded() && !grabbed()) {
      e.currentTarget.setPointerCapture(e.pointerId);
      beginPendingGrab(e.clientX, e.clientY);
      return;
    }
    const now = e.timeStamp;
    const isDouble = now - lastDownAt < DBL_MS && Math.abs(e.clientX - lastDownX) < DBL_TOL_PX;
    lastDownAt = now;
    lastDownX = e.clientX;
    if (isDouble) {
      lastDownAt = 0;
      setDragging(false);
      props.onInput(props.resetTo);
      props.onCommit(props.resetTo);
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    props.onInput(valueFromX(e.clientX));
  }
  function move(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      // Pre-grab: a swipe past tolerance cancels the pending grab (a brush).
      if (Math.abs(e.clientX - grabStartX) > GUARD_GRAB_MOVE_TOL ||
          Math.abs(e.clientY - grabStartY) > GUARD_GRAB_MOVE_TOL) {
        cancelPendingGrab();
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    props.onInput(valueFromX(e.clientX));
  }
  function up(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      cancelPendingGrab();
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
    setDragging(false);
    setGrabbed(false); // re-lock — a guarded slider needs a fresh grab next time
    props.onCommit(valueFromX(e.clientX));
  }

  const fillPct = () => ((props.value - props.min) / (props.max - props.min)) * 100;

  return (
    <div
      class={`mixer-hslider ${props.klass}`}
      data-dragging={dragging() ? 'true' : 'false'}
      data-grabbed={grabbed() ? 'true' : 'false'}
      ref={el}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
    >
      <div class="mixer-hslider-track" />
      <div class="mixer-hslider-fill" style={{ width: `${fillPct()}%` }} />
      <div class="mixer-hslider-handle" style={{ left: `calc(${fillPct()}% - 4px)` }} />
      <Show when={props.label}><span class="mixer-hslider-label">{props.label}</span></Show>
    </div>
  );
}

export function Mixer(props: MixerProps) {
  const stripCount = () => effectiveMixerStrips(props.item);

  // Feedback status (DEC-467): 'live' = device present + fresh; 'stale' =
  // present but no update in ~5s (NORMAL — the device sends on-change only);
  // 'none' = no OP-AbletonMixer device has ever connected (strips are the
  // operator's last local action, not Live's truth).
  const feedbackState = (): 'live' | 'stale' | 'none' => {
    const f = mixerFeedback();
    if (!f.connected || !f.hasEverReceived) return 'none';
    return f.stale ? 'stale' : 'live';
  };
  const feedbackTitle = (): string => {
    const f = mixerFeedback();
    if (feedbackState() === 'none') return 'No mixer device - strips show your last action, not Live';
    if (feedbackState() === 'stale') return `Synced ${Math.round(f.ageMs / 1000)}s ago`;
    return 'Live - strips reflect Ableton';
  };

  // No OP-AbletonMixer device has ever fed the bridge → the strips are only the
  // operator's last local action, not Live's truth. Blur them + overlay a
  // "waiting for the device" note. Suppressed in edit mode so the strips stay
  // clear while laying the item out (the device is usually offline at design time).
  const waiting = () => feedbackState() === 'none' && !isEditMode();
  // A single-track mixer is too narrow for "OP-ABLETONMIXER" on one line, so the
  // overlay stacks it OP-/ABLETON/MIXER there; 2+ tracks keep the name whole.
  const visibleStrips = () => (props.item.master ? 1 : stripCount());

  return (
    <div class="mixer" data-token={COLOR_TOKEN[props.item.color]} data-skin={props.item.skin ?? 'console'} data-master={props.item.master ? 'true' : 'false'} data-waiting={waiting() ? 'true' : 'false'}>
      <span
        class="mixer-feedback-dot"
        data-state={feedbackState()}
        title={feedbackTitle()}
        aria-label={feedbackTitle()}
      />
      <Show when={!props.item.master}>
        <button
          type="button"
          class="mixer-rescan"
          title="Rescan the bank - re-sync strip names after a Live track add / remove / reorder"
          onPointerDown={(e) => {
            if (editOwnsGesture(props.item.id)) return;
            e.stopPropagation();
            for (const target of fanoutTargets(props.item.bridgeIds)) void sendMixerRescan(target);
          }}
        >
          ⟳
        </button>
      </Show>
      <Show
        when={!props.item.master}
        fallback={<MixerStrip stripIndex={0} item={props.item} master />}
      >
        <Index each={Array.from({ length: stripCount() })}>
          {(_strip, i) => <MixerStrip stripIndex={i} item={props.item} returns={props.item.returns === true} />}
        </Index>
      </Show>
      <Show when={waiting()}>
        <div class="mixer-waiting" aria-live="polite">
          <div class="mixer-waiting-text">
            <span>WAITING</span>
            <span>FOR</span>
            <Show when={visibleStrips() === 1} fallback={<span>OP-ABLETONMIXER</span>}>
              <span>OP-</span>
              <span>ABLETON</span>
              <span>MIXER</span>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  );
}

function MixerStrip(props: { stripIndex: number; item: MixerItem; master?: boolean; returns?: boolean }) {
  const absIdx = () => {
    if (props.master) return MASTER_IDX;
    if (props.returns) return RETURN_BASE + props.stripIndex;
    return props.item.bankOffset + props.stripIndex;
  };
  const st = () => stripState(absIdx());

  const [dragging, setDragging] = createSignal(false);
  let faderEl: HTMLDivElement | undefined;

  // ── Per-strip send guard: <=1 in-flight + 1 pending, last-value-wins.
  let inFlight = false;
  let pending: number | null = null;
  let sendTimer: number | undefined;
  const MIN_SEND_MS = 30;

  function fire(): void {
    sendTimer = undefined;
    if (inFlight || pending === null) return;
    const v = pending;
    pending = null;
    inFlight = true;
    void Promise.all(
      fanoutTargets(props.item.bridgeIds).map((target) => sendMixerVolume(absIdx(), v, target)),
    ).finally(() => {
      inFlight = false;
      // Bound the send cadence: if a newer value queued while this was in
      // flight, send it no sooner than MIN_SEND_MS from now.
      if (pending !== null && sendTimer === undefined) {
        sendTimer = window.setTimeout(fire, MIN_SEND_MS);
      }
    });
  }
  function scheduleSend(v: number): void {
    pending = v;
    if (inFlight || sendTimer !== undefined) return;
    fire();
  }
  /** Guarantee the final value lands (release / grab commit), bypassing the
   *  cadence timer. */
  function forceSend(v: number): void {
    pending = v;
    if (sendTimer !== undefined) { clearTimeout(sendTimer); sendTimer = undefined; }
    if (!inFlight) fire();
  }
  onCleanup(() => { if (sendTimer !== undefined) clearTimeout(sendTimer); });

  // ── Show-mode guard: long-press-to-grab (mirrors Fader / DEC-072).
  const [grabbed, setGrabbed] = createSignal(false);
  let grabTimer: number | undefined;
  let grabStartY = 0;
  function beginPendingGrab(y: number): void {
    grabStartY = y;
    if (grabTimer !== undefined) clearTimeout(grabTimer);
    grabTimer = window.setTimeout(() => {
      grabTimer = undefined;
      setGrabbed(true);
      setDragging(true);
      commit(valueFromPointer(grabStartY));
    }, GUARD_GRAB_HOLD_MS);
  }
  function cancelPendingGrab(): void {
    if (grabTimer !== undefined) { clearTimeout(grabTimer); grabTimer = undefined; }
  }
  onCleanup(() => { if (grabTimer !== undefined) clearTimeout(grabTimer); });
  createEffect(() => { if (isEditMode()) { cancelPendingGrab(); setGrabbed(false); } });

  // ── Double-tap / double-click anywhere on the throw -> snap to unity (0 dB),
  //    mirroring Live's own "double-click a fader resets to default". Detected
  //    from pointerdown timing (NOT native dblclick) so it also works as a
  //    double-TAP on the iPad, where the captured pointer + preventDefault
  //    suppress the synthesized dblclick. Guarded performance-mode faders are exempt
  //    until grabbed (the guard must still block a stray stage tap).
  const DBL_TAP_MS = 300;
  const DBL_TAP_TOL_PX = 24;
  let lastDownAt = 0;
  let lastDownY = Number.NaN;

  function resetToUnity(): void {
    setDragging(false);
    setStripVolumeOptimistic(absIdx(), UNITY); // instant optimistic render
    forceSend(UNITY);                           // guarantee it lands (bypass cadence)
  }

  function valueFromPointer(clientY: number): number {
    if (!faderEl) return st().volume;
    const rect = faderEl.getBoundingClientRect();
    let frac = 1 - (clientY - rect.top) / rect.height; // bottom = 0, top = 1
    frac = Math.max(0, Math.min(1, frac));
    return applyUnityMagnet(positionToValue(frac));
  }

  function commit(v: number): void {
    setStripVolumeOptimistic(absIdx(), v); // instant render every frame
    scheduleSend(v);                        // guarded dispatch
  }

  function onFaderDown(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    e.preventDefault();
    const guarded = !isEditMode() && !!props.item.guard;
    if (guarded && !grabbed()) {
      e.currentTarget.setPointerCapture(e.pointerId);
      beginPendingGrab(e.clientY);
      return;
    }
    // A second down within DBL_TAP_MS, near the same spot, is a reset gesture —
    // snap to unity instead of starting a drag.
    const now = e.timeStamp;
    const isDouble = now - lastDownAt < DBL_TAP_MS
      && Math.abs(e.clientY - lastDownY) < DBL_TAP_TOL_PX;
    lastDownAt = now;
    lastDownY = e.clientY;
    if (isDouble) {
      lastDownAt = 0; // consume — a third quick tap starts a fresh pair
      resetToUnity();
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    commit(valueFromPointer(e.clientY));
  }

  function onFaderMove(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      // Pre-grab: a swipe past tolerance cancels the pending grab (a brush,
      // not a press-to-grab). Nothing sent.
      if (Math.abs(e.clientY - grabStartY) > GUARD_GRAB_MOVE_TOL) {
        cancelPendingGrab();
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    commit(valueFromPointer(e.clientY));
  }

  function onFaderUp(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      cancelPendingGrab();
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    setGrabbed(false); // re-lock — a guarded fader needs a fresh grab next time
    forceSend(st().volume); // ensure the final position always lands
  }

  function onMute(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    for (const target of fanoutTargets(props.item.bridgeIds)) {
      void setStripMuteLocal(absIdx(), !st().mute, target);
    }
  }

  // Master track shows volume + pan only ("Main", Live 11.1+ rename; LOM stays
  // `master_track`). Return tracks have volume/pan/mute/solo/sends but NO arm;
  // their label is the live name or an A/B/... placeholder.
  const displayName = () => {
    if (props.master) return 'Main';
    if (props.returns) return st().name.trim() !== '' ? st().name : String.fromCharCode(65 + props.stripIndex);
    return stripLabel(absIdx(), st().name);
  };

  // ── Extended controls: solo / arm (one-shot toggles, like mute) + pan /
  //    sends (continuous, each with its own guarded sender). Config-gated off
  //    the item; all default OFF, suppressed in master mode, and arm is also
  //    suppressed for returns (Live exposes no arm on returns).
  const showPan = () => props.item.showPan === true;
  const showSolo = () => !props.master && props.item.showSolo === true;
  const showArm = () => !props.master && !props.returns && props.item.showArm === true;
  const showSends = () => !props.master && props.item.showSends === true;
  const showMute = () => !props.master && props.item.showMute;
  // Prefer the device's live per-track send count (`/sendcount` feedback = the
  // set's return count) so the strip right-sizes to Live; fall back to the
  // item's operator-set count before feedback arrives.
  const sendCount = () => Math.max(1, Math.min(12, st().sendCount ?? props.item.sendCount ?? 2));

  // Continuous controls (pan / sends): edit mode gives the gesture to the
  // wrapper; a guard requires a long-press-to-grab (handled in HSlider,
  // mirroring the fader / DEC-072).
  const editLocked = () => editOwnsGesture(props.item.id);
  const contGuarded = () => !isEditMode() && !!props.item.guard;

  const panGuard = makeSendGuard((v) =>
    Promise.all(fanoutTargets(props.item.bridgeIds).map((target) => sendMixerPan(absIdx(), v, target))),
  );
  onCleanup(() => panGuard.cleanup());
  function panInput(v: number): void { setStripPanOptimistic(absIdx(), v); panGuard.schedule(v); }
  function panCommit(v: number): void { setStripPanOptimistic(absIdx(), v); panGuard.force(v); }

  const sendGuards = new Map<number, SendGuard>();
  function sendGuard(i: number): SendGuard {
    let g = sendGuards.get(i);
    if (!g) {
      g = makeSendGuard((v) =>
        Promise.all(fanoutTargets(props.item.bridgeIds).map((target) => sendMixerSend(absIdx(), i, v, target))),
      );
      sendGuards.set(i, g);
    }
    return g;
  }
  onCleanup(() => { for (const g of sendGuards.values()) g.cleanup(); });
  function sendInput(i: number, v: number): void { setStripSendOptimistic(absIdx(), i, v); sendGuard(i).schedule(v); }
  function sendCommit(i: number, v: number): void { setStripSendOptimistic(absIdx(), i, v); sendGuard(i).force(v); }

  function onSolo(e: PointerEvent): void {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    for (const target of fanoutTargets(props.item.bridgeIds)) {
      void setStripSoloLocal(absIdx(), !st().solo, target);
    }
  }
  function onArm(e: PointerEvent): void {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    for (const target of fanoutTargets(props.item.bridgeIds)) {
      void setStripArmLocal(absIdx(), !st().arm, target);
    }
  }

  const posPct = () => valueToPosition(st().volume) * 100;

  return (
    <div class="mixer-strip" data-muted={st().mute ? 'true' : 'false'}>
      <Show when={props.item.showNames}>
        <div class="mixer-strip-name" title={displayName()}>
          {displayName()}
        </div>
      </Show>
      <Show when={showPan()}>
        <HSlider
          klass="mixer-pan"
          value={st().pan}
          min={-1} max={1} detent={0} resetTo={0}
          label="L·R"
          editLocked={editLocked}
          guarded={contGuarded}
          onInput={panInput}
          onCommit={panCommit}
        />
      </Show>
      <div class="mixer-strip-body">
        <Show when={props.item.showMeters}>
          <div class="mixer-meter">
            <div class="mixer-meter-fill" style={{ height: `${Math.round(st().meter * 100)}%` }} />
          </div>
        </Show>
        <div
          class="mixer-fader"
          data-dragging={dragging() ? 'true' : 'false'}
          data-grabbed={grabbed() ? 'true' : 'false'}
          ref={faderEl}
          onPointerDown={onFaderDown}
          onPointerMove={onFaderMove}
          onPointerUp={onFaderUp}
          onPointerCancel={onFaderUp}
        >
          <div class="mixer-fader-track" />
          <div class="mixer-fader-fill" style={{ height: `${posPct()}%` }} />
          <div class="mixer-unity-tick" style={{ bottom: `${UNITY * 100}%` }} />
          <div class="mixer-fader-handle" style={{ bottom: `calc(${posPct()}% - 10px)` }} />
        </div>
      </div>
      <Show when={showSends()}>
        <div class="mixer-sends">
          <Index each={Array.from({ length: sendCount() })}>
            {(_send, i) => (
              <HSlider
                klass="mixer-send"
                value={st().sends[i] ?? 0}
                min={0} max={1} resetTo={0}
                label={String.fromCharCode(65 + i)}
                editLocked={editLocked}
                guarded={contGuarded}
                onInput={(v) => sendInput(i, v)}
                onCommit={(v) => sendCommit(i, v)}
              />
            )}
          </Index>
        </div>
      </Show>
      <div class="mixer-strip-db">{dbLabel(st().volume)}</div>
      <Show when={showSolo() || showArm() || showMute()}>
        <div class="mixer-btn-row">
          <Show when={showSolo()}>
            <button
              type="button"
              class="mixer-btn mixer-solo"
              data-on={st().solo ? 'true' : 'false'}
              onPointerDown={onSolo}
              title={`Solo ${stripLabel(absIdx(), st().name)}`}
            >
              S
            </button>
          </Show>
          <Show when={showArm()}>
            <button
              type="button"
              class="mixer-btn mixer-arm"
              data-on={st().arm ? 'true' : 'false'}
              onPointerDown={onArm}
              title={`Arm ${stripLabel(absIdx(), st().name)}`}
            >
              ●
            </button>
          </Show>
          <Show when={showMute()}>
            <button
              type="button"
              class="mixer-btn mixer-mute"
              data-on={st().mute ? 'true' : 'false'}
              onPointerDown={onMute}
              title={`Mute ${stripLabel(absIdx(), st().name)}`}
            >
              M
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
}
